import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useLuxeGrid, useLuxeReveal } from '@gjl/motion/css'
import { ArrowRight, FileBarChart2, ShieldCheck, LineChart, Coins, AlertTriangle } from 'lucide-react'

type Question = {
  icon: React.ElementType
  question: string
  why: string
  good: string
  fairmeadow: string
  proofLabel: string
  proofHref: string
}

const questions: Question[] = [
  {
    icon: FileBarChart2,
    question: 'What does your reporting cadence actually look like?',
    why: 'Vague reporting promises ("we keep investors informed") are the most common red flag. Sophisticated LPs want to see the actual artifact before they wire capital.',
    good: 'A specific cadence (e.g. quarterly), specific contents (property-level P&L, rent roll, occupancy, market conditions), and specific delivery (PDF, dashboard, email).',
    fairmeadow:
      'Quarterly property-level reports covering rent roll, occupancy, operating P&L, capex, and market notes. Investors see the full artifact for Deal #1 on the Track Record page before they ever commit capital.',
    proofLabel: 'See the Track Record',
    proofHref: '/track-record',
  },
  {
    icon: ShieldCheck,
    question: 'How do you stress-test a deal before closing?',
    why: '"Conservative underwriting" is claimed by every operator in the space. The only meaningful version of the answer names specific adverse scenarios the model survives.',
    good: 'Explicit scenarios: rising vacancy, rate hikes at refinance, deferred maintenance, rent stagnation. The pro forma must still produce positive cash flow under each.',
    fairmeadow:
      'Every deal is modeled against rising vacancies, rate hikes, and deferred maintenance before we offer. If the numbers break under pressure, we pass — full stop. The framework lives on the Resources page.',
    proofLabel: 'Read the Underwriting Framework',
    proofHref: '/resources/underwriting-basics',
  },
  {
    icon: LineChart,
    question: "What's your track record — and is it public?",
    why: 'Paper underwriting is different from a real closed deal. Many operators talk in pipeline. Few publish actual closed-deal financials.',
    good: 'A complete list of closed deals with purchase price, financing structure, current rent, occupancy, and operating performance against the original underwriting.',
    fairmeadow:
      "One deal closed (West Boulevard duplex, April 2025). Full purchase, financing, rent, and occupancy on the public Track Record page — not gated behind an investor portal. We'd rather be honest about being early than pad the deck.",
    proofLabel: 'View Deal #1',
    proofHref: '/track-record',
  },
  {
    icon: Coins,
    question: 'Where is your own capital at risk in each deal?',
    why: "Operator alignment is structural, not philosophical. If they don't have meaningful capital next to yours, their incentive is volume — not outcomes.",
    good: 'Founder/GP equity contribution in every deal, disclosed at the deal level. Not just acquisition fees and a promote.',
    fairmeadow:
      'Founder equity in every deal. Garrett invests his own capital alongside LPs in each acquisition — not as a sweep into a fund-level vehicle. The percentages and absolute dollars are disclosed in each deal memo.',
    proofLabel: "Read Garrett's Story",
    proofHref: '/about',
  },
  {
    icon: AlertTriangle,
    question: 'What happens if a deal underperforms the underwriting?',
    why: 'Most operators only rehearse the success pitch. The adverse-scenario answer reveals whether they have a real plan or just hope.',
    good: 'A written plan: trigger metrics, communication cadence under stress, capital-call vs. distribution-pause vs. refinance options, and how decisions get made.',
    fairmeadow:
      'A written adverse-scenario plan attached to every deal memo: trigger thresholds (occupancy, DSCR), monthly communication (instead of quarterly) once a threshold is crossed, and a clear hierarchy — preserve capital, then preserve cash flow, then optimize for return.',
    proofLabel: 'See the Strategy',
    proofHref: '/strategy',
  },
]

export default function OperatorQuestionsPage() {
  const heroRef = useLuxeHero<HTMLDivElement>(0.2)
  const gridRef = useLuxeGrid<HTMLDivElement>('.question-card')
  const ctaRef = useLuxeReveal<HTMLDivElement>('.reveal')

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="bg-gradient-dark-gold absolute inset-0" />
        <div ref={heroRef} className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <p className="text-primary mb-6 text-lg font-medium tracking-wide">For investors</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">Five questions to ask any real estate operator.</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Use these to vet anyone you're considering investing with — including us. We've answered each one with what
            you should be looking for, and what we actually do.
          </p>
        </div>
      </section>

      {/* Questions Section */}
      <section ref={gridRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl space-y-8">
          {questions.map((q, idx) => {
            const Icon = q.icon
            return (
              <GlassCard key={q.question} accent className="question-card">
                <div className="mb-6 flex items-start gap-4">
                  <div className="bg-primary/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
                    <Icon className="text-primary h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-primary mb-2 text-sm font-semibold tracking-wide">Question {idx + 1}</p>
                    <h2 className="text-foreground text-2xl font-bold md:text-3xl">{q.question}</h2>
                  </div>
                </div>

                <div className="space-y-5 pl-0 md:pl-16">
                  <div>
                    <p className="text-primary mb-2 text-sm font-semibold tracking-wide uppercase">Why it matters</p>
                    <p className="text-muted-foreground">{q.why}</p>
                  </div>

                  <div>
                    <p className="text-primary mb-2 text-sm font-semibold tracking-wide uppercase">
                      What a good answer looks like
                    </p>
                    <p className="text-muted-foreground">{q.good}</p>
                  </div>

                  <div className="border-primary border-l-2 py-1 pl-5">
                    <p className="text-primary mb-2 text-sm font-semibold tracking-wide uppercase">
                      How Fairmeadow answers
                    </p>
                    <p className="text-foreground mb-4">{q.fairmeadow}</p>
                    <Link
                      to={q.proofHref}
                      className="text-primary hover:text-primary/80 group inline-flex items-center font-medium transition-colors"
                    >
                      {q.proofLabel}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </GlassCard>
            )
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent className="reveal text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">Want to ask us something else?</h2>
            <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
              Take the fit assessment, or send a direct message. Either way you'll hear from Garrett within 1-2 business
              days.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/fit">
                <Button size="xl">
                  Take the Fit Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="xl">
                  Send a Message
                </Button>
              </Link>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
